import type {
  MerchantStoreBinding,
  MerchantStoreBindingsResponse,
  MerchantStorePlatform,
} from "@/features/settings/storeManagementSchemas";

export const storeBindingGroupOrder = [
  "sync_error",
  "high_risk",
  "pending",
  "stable",
] as const;

export type StoreBindingGroupKey = (typeof storeBindingGroupOrder)[number];

export const storeBindingGroupLabels: Readonly<
  Record<StoreBindingGroupKey, string>
> = {
  high_risk: "高危店铺",
  pending: "未处理代办",
  stable: "经营平稳",
  sync_error: "绑定/同步 异常",
};

export interface StorePlatformGroup {
  bindings: MerchantStoreBinding[];
  platform: MerchantStorePlatform;
}

export function classifyStoreBinding(
  binding: MerchantStoreBinding,
): StoreBindingGroupKey {
  if (binding.syncStatus !== "healthy") return "sync_error";
  if (binding.riskLevel === "high" || binding.riskCount > 0) {
    return "high_risk";
  }
  if (binding.pendingTodoCount > 0) return "pending";
  return "stable";
}

export function groupStoreBindingsByPlatform(
  response: MerchantStoreBindingsResponse,
): StorePlatformGroup[] {
  const platformByProvider = new Map(
    response.platforms.map((platform) => [platform.provider, platform]),
  );
  const bindingsByProvider = new Map<string, MerchantStoreBinding[]>();

  for (const binding of response.bindings) {
    const current = bindingsByProvider.get(binding.provider) ?? [];
    current.push(binding);
    bindingsByProvider.set(binding.provider, current);
  }

  const missingProviders = Array.from(bindingsByProvider.keys())
    .filter((provider) => !platformByProvider.has(provider))
    .sort();
  const platforms = [
    ...response.platforms,
    ...missingProviders.map((provider) => ({
      bindingSupported: false,
      displayName: provider,
      provider,
    })),
  ];

  return platforms.map((platform) => ({
    bindings: [...(bindingsByProvider.get(platform.provider) ?? [])].sort(
      (left, right) =>
        right.updatedAt.localeCompare(left.updatedAt) ||
        left.displayName.localeCompare(right.displayName, "zh-CN"),
    ),
    platform,
  }));
}

export function partitionStoreBindings(
  bindings: ReadonlyArray<MerchantStoreBinding>,
) {
  const result: Record<StoreBindingGroupKey, MerchantStoreBinding[]> = {
    high_risk: [],
    pending: [],
    stable: [],
    sync_error: [],
  };
  for (const binding of bindings) {
    result[classifyStoreBinding(binding)].push(binding);
  }
  return result;
}
